import { getApiBase, getToken } from "./api";

interface ChatSource {
  file_id: number;
  file_name: string;
  content: string;
  type: string;
  relevance: number;
}

interface AskResponse {
  answer: string;
  sources: ChatSource[] | null;
  method: string | null;
}

interface ChatHistoryItem {
  id: number;
  session_id?: string | null;
  parent_id?: number | null;
  question: string;
  answer: string;
  file_ids: number[];
  sources: ChatSource[];
  created_at: string;
}

interface ProgressData {
  step: string;
  message: string;
  files?: { id: number; name: string; type: string }[];
  selected_files?: string[];
  file_name?: string;
  chars?: number;
  node_ids?: string[];
  thinking?: string;
}

interface FileStartData {
  file_id: number;
  file_name: string;
  method: string;
  context_chars: number;
}

interface FileContentData {
  file_id: number;
  text: string;
}

interface FileDoneData {
  file_id: number;
  file_name: string;
  method: string;
}

interface SSECallbacks {
  onContent?: (text: string) => void;
  onSources?: (docs: ChatSource[]) => void;
  onWebResults?: (results: unknown[]) => void;
  onProgress?: (data: ProgressData) => void;
  onFileStart?: (data: FileStartData) => void;
  onFileContent?: (data: FileContentData) => void;
  onFileDone?: (data: FileDoneData) => void;
  onDone?: (data: { method?: string; file_count?: number; elapsed?: number; session_id?: string; message_id?: number }) => void;
  onError?: (message: string) => void;
}

async function askStream(
  question: string,
  callbacks: SSECallbacks,
  opts?: { file_ids?: number[]; web_search?: boolean; signal?: AbortSignal; session_id?: string | null; parent_id?: number | null }
): Promise<void> {
  const token = getToken();
  const apiBase = getApiBase();

  const response = await fetch(`${apiBase}/chat/ask/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      question,
      file_ids: opts?.file_ids || null,
      web_search: opts?.web_search || false,
      session_id: opts?.session_id || null,
      parent_id: opts?.parent_id ?? null,
    }),
    signal: opts?.signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.detail || `请求失败 (${response.status})`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    let eventType = "";
    for (const line of lines) {
      if (line.startsWith("event: ")) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        const dataStr = line.slice(6);
        try {
          const data = JSON.parse(dataStr);
          switch (eventType) {
            case "content":
              callbacks.onContent?.(data.text);
              break;
            case "sources":
              callbacks.onSources?.(data.documents);
              break;
            case "web_results":
              callbacks.onWebResults?.(data.results);
              break;
            case "progress":
              callbacks.onProgress?.(data as ProgressData);
              break;
            case "file_start":
              callbacks.onFileStart?.(data as FileStartData);
              break;
            case "file_content":
              callbacks.onFileContent?.(data as FileContentData);
              break;
            case "file_done":
              callbacks.onFileDone?.(data as FileDoneData);
              break;
            case "done":
              callbacks.onDone?.(data);
              break;
            case "error":
              callbacks.onError?.(data.message);
              break;
          }
        } catch {
          // skip malformed JSON
        }
      }
    }
  }
}

async function getChatHistory(
  skip = 0,
  limit = 20
): Promise<ChatHistoryItem[]> {
  const token = getToken();
  const apiBase = getApiBase();
  const response = await fetch(
    `${apiBase}/chat/history?skip=${skip}&limit=${limit}`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.json();
}

async function getChatHistoryCount(): Promise<{ total: number }> {
  const token = getToken();
  const apiBase = getApiBase();
  const response = await fetch(`${apiBase}/chat/history/count`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return response.json();
}

export type { ChatSource, AskResponse, ChatHistoryItem, SSECallbacks, ProgressData, FileStartData, FileContentData, FileDoneData };
export { askStream, getChatHistory, getChatHistoryCount };
